import React, { useState, useRef } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Navbar from './Navbar';

// Compute SHA-256 hash entirely in the browser — photo never leaves device
async function computeSHA256(file) {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export default function VerifyPage() {
  const [proofId, setProofId] = useState('');
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState(null);
  const [error, setError] = useState('');

  // Photo hash verification state
  const [photoFile, setPhotoFile] = useState(null);
  const [photoChecking, setPhotoChecking] = useState(false);
  const [photoResult, setPhotoResult] = useState(null); // 'match' | 'mismatch' | null
  const [computedHash, setComputedHash] = useState('');
  const fileInputRef = useRef(null);

  const handleVerify = async () => {
    const id = proofId.trim().toUpperCase();
    if (!id) { setError('Please enter a Proof ID'); return; }
    if (!id.startsWith('SS-')) { setError('Proof IDs start with SS- e.g. SS-A3F2C1B0'); return; }
    setLoading(true);
    setRecord(null);
    setError('');
    setPhotoResult(null);
    setPhotoFile(null);
    setComputedHash('');
    try {
      const docSnap = await getDoc(doc(db, 'proof_records', id));
      if (docSnap.exists()) {
        setRecord(docSnap.data());
      } else {
        setError('No proof record found for this ID. It may be invalid or the photo was not taken with SafeSnap.');
      }
    } catch (e) {
      setError('Unable to connect. Please try again.');
    }
    setLoading(false);
  };

  const handlePhotoSelect = async (file) => {
    if (!file || !record) return;
    setPhotoFile(file);
    setPhotoChecking(true);
    setPhotoResult(null);
    setComputedHash('');
    try {
      const hash = await computeSHA256(file);
      setComputedHash(hash);
      if (hash === record.fileHash) {
        setPhotoResult('match');
      } else {
        setPhotoResult('mismatch');
      }
    } catch (e) {
      setPhotoResult('error');
    }
    setPhotoChecking(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handlePhotoSelect(file);
  };

  return (
    <>
      <Navbar />
      <div className="container" style={{ maxWidth: 680, padding: '60px 24px' }}>

        {/* Header */}
        <div style={{ marginBottom: 40 }}>
          <div className="badge badge-accent" style={{ marginBottom: 14 }}>Tamper check</div>
          <h1 style={{ fontFamily: 'var(--display)', fontSize: 'clamp(28px, 5vw, 48px)', fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 12 }}>
            Verify a SafeSnap photo
          </h1>
          <p style={{ fontSize: 16, color: 'var(--muted)', lineHeight: 1.7 }}>
            Two steps for complete verification. Step 1 confirms the capture. Step 2 confirms the photo was not edited.
          </p>
        </div>

        {/* STEP 1 — Proof ID */}
        <div style={{ background: 'white', border: '0.5px solid rgba(10,10,15,0.1)', borderRadius: 16, padding: 24, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: record ? '#00C88A' : 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ color: 'white', fontSize: 12, fontWeight: 700 }}>{record ? '✓' : '1'}</span>
            </div>
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>Verify the Proof ID</h3>
              <p style={{ fontSize: 12, color: 'var(--muted)' }}>Confirms the capture happened at the claimed time and location</p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <input
              className="verify-input"
              placeholder="SS-A3F2C1B0"
              value={proofId}
              onChange={e => { setProofId(e.target.value.toUpperCase()); setError(''); setRecord(null); setPhotoResult(null); }}
              onKeyDown={e => e.key === 'Enter' && handleVerify()}
              style={{ flex: 1 }}
            />
            <button className="btn-primary" onClick={handleVerify} disabled={loading} style={{ whiteSpace: 'nowrap', opacity: loading ? 0.6 : 1 }}>
              {loading ? 'Checking...' : 'Check ID'}
            </button>
          </div>

          {error && (
            <div style={{ marginTop: 12, background: 'rgba(255,77,46,0.08)', border: '1px solid rgba(255,77,46,0.2)', borderRadius: 10, padding: '12px 14px' }}>
              <p style={{ color: 'var(--red)', fontSize: 13, fontFamily: 'var(--mono)' }}>✗  {error}</p>
            </div>
          )}

          {/* Proof ID result */}
          {record && (
            <div style={{ marginTop: 16 }}>
              <div style={{ background: 'rgba(0,200,138,0.08)', border: '1px solid rgba(0,200,138,0.25)', borderRadius: 12, padding: '14px 16px', marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ fontSize: 20 }}>✅</span>
                <div>
                  <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)', marginBottom: 2 }}>Proof record found</p>
                  <p style={{ fontSize: 12, color: 'var(--muted)' }}>Capture confirmed in SafeSnap database</p>
                </div>
              </div>

              {/* Record details */}
              <div style={{ background: 'var(--ink2)', borderRadius: 12, padding: 16, marginBottom: 14 }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 20, fontWeight: 800, color: 'white', marginBottom: 12 }}>{record.proofId}</div>
                {[
                  { k: 'TIMESTAMP', v: record.timestamp },
                  { k: 'GPS', v: `${record.latitude}, ${record.longitude}` },
                  { k: 'USE CASE', v: record.useCase },
                  { k: 'DEVICE', v: record.device },
                ].map((r, i) => (
                  <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'rgba(255,255,255,0.3)', minWidth: 80, paddingTop: 1 }}>{r.k}</span>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'rgba(255,255,255,0.85)' }}>{r.v}</span>
                  </div>
                ))}
              </div>

              {/* WARNING — this is not complete */}
              <div style={{ background: '#FFF8E1', border: '1px solid #F9A825', borderRadius: 12, padding: '14px 16px' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
                  <div>
                    <p style={{ fontWeight: 700, fontSize: 13, color: '#856404', marginBottom: 6 }}>
                      Proof ID alone is NOT complete verification
                    </p>
                    <p style={{ fontSize: 12, color: '#856404', lineHeight: 1.6, marginBottom: 8 }}>
                      This confirms a photo was captured with these details. It does <strong>not</strong> confirm that the photo you received matches this record. Someone could share this Proof ID with a different or edited photo.
                    </p>
                    <p style={{ fontSize: 12, color: '#856404', fontWeight: 600 }}>
                      Complete Step 2 below to verify the actual photo file.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* STEP 2 — Photo hash check */}
        <div style={{
          background: record ? 'white' : 'rgba(255,255,255,0.5)',
          border: `0.5px solid ${record ? 'rgba(10,10,15,0.1)' : 'rgba(10,10,15,0.05)'}`,
          borderRadius: 16, padding: 24, marginBottom: 16,
          opacity: record ? 1 : 0.5,
          transition: 'all 0.3s',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: photoResult === 'match' ? '#00C88A' : photoResult === 'mismatch' ? '#FF4D2E' : record ? 'var(--ink)' : 'rgba(10,10,15,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.3s' }}>
              <span style={{ color: 'white', fontSize: 12, fontWeight: 700 }}>
                {photoResult === 'match' ? '✓' : photoResult === 'mismatch' ? '✗' : '2'}
              </span>
            </div>
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>Verify the photo file</h3>
              <p style={{ fontSize: 12, color: 'var(--muted)' }}>
                Confirms the photo has not been edited — computed entirely in your browser, photo never uploaded
              </p>
            </div>
          </div>

          {/* Drop zone */}
          <div
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            onClick={() => record && fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${photoResult === 'match' ? '#00C88A' : photoResult === 'mismatch' ? '#FF4D2E' : 'rgba(10,10,15,0.15)'}`,
              borderRadius: 12,
              padding: '28px 20px',
              textAlign: 'center',
              cursor: record ? 'pointer' : 'not-allowed',
              background: photoResult === 'match' ? 'rgba(0,200,138,0.04)' : photoResult === 'mismatch' ? 'rgba(255,77,46,0.04)' : 'var(--surface)',
              transition: 'all 0.3s',
              marginBottom: 14,
            }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={e => e.target.files[0] && handlePhotoSelect(e.target.files[0])}
            />
            {photoChecking ? (
              <div>
                <div className="spinner" style={{ margin: '0 auto 12px' }} />
                <p style={{ fontSize: 13, color: 'var(--muted)' }}>Computing SHA-256 fingerprint in your browser...</p>
              </div>
            ) : photoFile && photoResult ? (
              <div>
                {photoResult === 'match' ? (
                  <>
                    <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
                    <p style={{ fontWeight: 700, fontSize: 16, color: '#00C88A', marginBottom: 6 }}>Photo is genuine</p>
                    <p style={{ fontSize: 13, color: 'var(--muted)' }}>
                      This photo is byte-for-byte identical to the original capture. It has not been edited.
                    </p>
                  </>
                ) : photoResult === 'mismatch' ? (
                  <>
                    <div style={{ fontSize: 40, marginBottom: 10 }}>❌</div>
                    <p style={{ fontWeight: 700, fontSize: 16, color: '#FF4D2E', marginBottom: 6 }}>Photo does not match</p>
                    <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 10 }}>
                      The fingerprint of this photo is different from the original. The photo may have been edited, re-saved, or compressed.
                    </p>
                    <button
                      onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}
                      style={{ background: 'none', border: '1px solid rgba(10,10,15,0.2)', borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer', color: 'var(--muted)' }}>
                      Try a different photo
                    </button>
                  </>
                ) : (
                  <p style={{ fontSize: 13, color: 'var(--red)' }}>Error reading file. Please try again.</p>
                )}
              </div>
            ) : (
              <>
                <div style={{ fontSize: 32, marginBottom: 10 }}>📸</div>
                <p style={{ fontWeight: 600, fontSize: 14, color: record ? 'var(--ink)' : 'var(--muted)', marginBottom: 4 }}>
                  {record ? 'Drop the photo here or tap to select' : 'Complete Step 1 first'}
                </p>
                <p style={{ fontSize: 12, color: 'var(--muted)' }}>
                  SHA-256 computed in your browser — photo never leaves your device
                </p>
              </>
            )}
          </div>

          {/* Hash comparison detail */}
          {computedHash && record && (
            <div style={{ background: 'var(--surface)', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ marginBottom: 10 }}>
                <p style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: 4 }}>ORIGINAL FINGERPRINT (stored in SafeSnap cloud)</p>
                <p style={{ fontFamily: 'var(--mono)', fontSize: 10, color: photoResult === 'match' ? '#00C88A' : 'var(--red)', wordBreak: 'break-all', lineHeight: 1.5 }}>{record.fileHash}</p>
              </div>
              <div>
                <p style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: 4 }}>PHOTO YOU SUBMITTED</p>
                <p style={{ fontFamily: 'var(--mono)', fontSize: 10, color: photoResult === 'match' ? '#00C88A' : 'var(--red)', wordBreak: 'break-all', lineHeight: 1.5 }}>{computedHash}</p>
              </div>
            </div>
          )}
        </div>

        {/* FINAL VERDICT */}
        {record && photoResult && (
          <div style={{
            background: photoResult === 'match' ? 'rgba(0,200,138,0.08)' : 'rgba(255,77,46,0.08)',
            border: `2px solid ${photoResult === 'match' ? 'rgba(0,200,138,0.3)' : 'rgba(255,77,46,0.3)'}`,
            borderRadius: 16, padding: 24, textAlign: 'center', marginBottom: 24,
          }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>
              {photoResult === 'match' ? '✅' : '❌'}
            </div>
            <h2 style={{ fontFamily: 'var(--display)', fontSize: 24, fontWeight: 800, color: 'var(--ink)', marginBottom: 8 }}>
              {photoResult === 'match' ? 'Fully verified — 100% authentic' : 'Verification failed — photo does not match'}
            </h2>
            <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.7 }}>
              {photoResult === 'match'
                ? 'Both the Proof ID and the photo file have been verified. This photo was taken with SafeSnap, at the stated time and location, and has not been edited in any way.'
                : 'The Proof ID exists but the photo you submitted does not match the original. The photo may have been edited, compressed or replaced after capture.'}
            </p>
          </div>
        )}

        {/* Help tips */}
        {!record && (
          <div style={{ padding: '28px 0', borderTop: '0.5px solid rgba(10,10,15,0.1)' }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Where do I find the Proof ID?</h3>
            {[
              'The Proof ID is printed in the stamp bar at the bottom of every SafeSnap photo (e.g. SS-A3F2C1B0)',
              'It is shown in the SafeSnap app after taking a photo — tap the copy icon to copy it',
              'When someone shares a SafeSnap photo they should include the Proof ID in their message',
              'You can also scan the QR code printed on the photo — it opens this page automatically',
            ].map((tip, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                <span style={{ color: 'var(--accent)', fontWeight: 700, flexShrink: 0 }}>→</span>
                <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.5 }}>{tip}</p>
              </div>
            ))}
            <div style={{ marginTop: 20, padding: 16, background: 'var(--surface2)', borderRadius: 12 }}>
              <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 12 }}>
                For strongest verification download the SafeSnap app — it performs a full 5-layer forensic analysis.
              </p>
              <a href="https://play.google.com/store/apps/details?id=com.safesnap.app" target="_blank" rel="noreferrer">
                <button className="btn-primary" style={{ fontSize: 13, padding: '10px 20px' }}>
                  Download SafeSnap
                </button>
              </a>
            </div>
          </div>
        )}
      </div>

      <footer>
        <div className="footer-inner">
          <div className="footer-brand">SafeSnap</div>
          <div className="footer-copy">safesnap.app@gmail.com</div>
        </div>
      </footer>
    </>
  );
}
